import { useCallback, useEffect, useRef, useState } from 'react';
import prefersReducedMotion from 'utils/prefersReducedMotion';

export const ITEM_LEAVE_ANIMATION_NAME = 'itemLeave';
export const ROW_LEAVE_ANIMATION_NAME = 'rowLeave';
export const ITEM_LEAVE_CLASS = 'motion-safe:animate-itemLeave';
export const ROW_LEAVE_CLASS = 'motion-safe:animate-rowLeave';
// Commits an item whose animationend never came: hidden mid-exit, dropped by a
// refetch, or a browser that skips the animation (React 18 has no
// onAnimationCancel prop). Longer than the 350 ms exit.
export const LEAVE_FALLBACK_MS = 700;

const LEAVE_ANIMATION_NAMES = [
  ITEM_LEAVE_ANIMATION_NAME,
  ROW_LEAVE_ANIMATION_NAME,
];

/**
 * Lets an item play its exit before the state update that removes it.
 *
 * `leave(id, commit)` marks the item as leaving and runs `commit` (the update
 * that removes it) once its exit animation ends, or at once under reduced
 * motion. A second `leave` for an item already leaving is ignored, so `commit`
 * runs once; it should still filter by id, as a refetch may have dropped the
 * item meanwhile. `leave` returns false when it ignored the call.
 *
 * `cancel(id)` brings back an item still leaving, without its `commit`: for an
 * exit started before the server answered, when the server refuses.
 *
 * Render the item with `isLeaving(id)` (components/LeavingItem, or
 * ROW_LEAVE_CLASS on a table row) and put `onLeaveEnd(id)` on the element that
 * carries the exit animation.
 */
const useLeavingItems = () => {
  const [leavingIds, setLeavingIds] = useState(() => new Set());
  const pendingRef = useRef(new Map());

  const finish = useCallback((id) => {
    const pending = pendingRef.current.get(id);
    if (!pending) return;
    pendingRef.current.delete(id);
    clearTimeout(pending.timer);
    setLeavingIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    pending.commit();
  }, []);

  const leave = useCallback(
    (id, commit) => {
      if (pendingRef.current.has(id)) return false;
      if (prefersReducedMotion()) {
        commit();
        return true;
      }
      const timer = setTimeout(() => finish(id), LEAVE_FALLBACK_MS);
      pendingRef.current.set(id, { commit, timer });
      setLeavingIds((current) => new Set(current).add(id));
      return true;
    },
    [finish],
  );

  const cancel = useCallback((id) => {
    const pending = pendingRef.current.get(id);
    if (!pending) return;
    pendingRef.current.delete(id);
    clearTimeout(pending.timer);
    setLeavingIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  // A list unmounted mid-exit still gets its removal: the commit may update
  // state that outlives it (a parent's list, the context).
  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.forEach(({ commit, timer }) => {
        clearTimeout(timer);
        commit();
      });
      pending.clear();
    };
  }, []);

  const isLeaving = useCallback((id) => leavingIds.has(id), [leavingIds]);

  const onLeaveEnd = useCallback(
    (id) => (event) => {
      // The item's own animations (entrance, flash) bubble up here too.
      if (event.target !== event.currentTarget) return;
      if (!LEAVE_ANIMATION_NAMES.includes(event.animationName)) return;
      finish(id);
    },
    [finish],
  );

  return { leave, cancel, isLeaving, onLeaveEnd };
};

export default useLeavingItems;
